import React from 'react';
import dayjs from 'dayjs';
import ls from 'localstorage-slim';
import {
  StyledCardPaymentTypeCardIconSpace
} from '../../components/pages/CardsPage/CardPaymentType/StyledCardPaymentType';
import {WithLessCreditCardIcon} from '../../icons';
import {
  cardBackendStatusesConstants as cardBackedStatus,
  cardStatusesConstants as cardStatus,
  KYCLevelStatusesConstants as kycLevelStatus,
  KYCReviewStatusesConstants as kycReviewStatus,
  cardVerificationLightStatusesConstants as lightStatus,
  cardLimitTypesList,
  cardLimitPeriodConstants,
  subscriptionFormFields,
  cardTypesConstants,
  cardDigitalizationStatusConstants,
  subscriptionBillTypesConstants
} from '../../constants';
import StatusMark from '../../components/StatusMark';
import {amountHelpers} from './amount.helpers';
import {fileHelpers} from './file.helpers';
import {OptionsList} from './form.helpers';
import {localizationHelpers} from './localization.helpers';
import {objectHelpers} from './object.helpers';
import routes from '../../routes/routes.json';

const {ACTIVE, FROZEN, PENDING, TERMINATED} = cardStatus;

// Treezor issues Mastercards, so a recognised number should start with a 5. Used only to build a
// repair candidate for the S/5 confusion OCR makes on the first digit — never applied blind.
const MASTERCARD_PREFIX = '5';

const {
  billedFieldName,
  budgetLimitFieldName,
  cardLimitFieldName,
  cardLimitPeriodFieldName,
  limitPeriodShortFieldName,
} = subscriptionFormFields;

const storedCardTableColumns = 'stored-cards-table-columns';

const cardTypeKeys = {
  [cardTypesConstants.FOR_PERSONAL_USE]: 'cardTypes.forPersonalUse',
  [cardTypesConstants.FOR_SUBSCRIPTION]: 'cardTypes.forSubscriptions',
};

const cardStatuses = {
  [cardBackedStatus.DESTROYED]: {
    key: 'cardStatuses.terminated',
    color: '#FF7875'
  },
  [cardBackedStatus.LOCK]: {
    key: 'cardStatuses.paused',
    color: '#FFCC00'
  },
  [cardBackedStatus.LOST]: {
    key: 'cardStatuses.terminated',
    color: '#FF7875'
  },
  [cardBackedStatus.UNLOCK]: {
    key: 'cardStatuses.active',
    color: '#52C41A'
  },
  [cardBackedStatus.STOLEN]: {
    key: 'cardStatuses.terminated',
    color: '#FF7875'
  },
  [cardBackedStatus.WAITING]: {
    key: 'cardStatuses.waiting',
    color: '#FFCC00'
  },
};

/**
 * Returns a list of card type select options with the given value marked as active.
 * @param {string|number} activeValue - Currently selected card type value.
 * @returns {Array<{value: string, label: string, isActive: boolean}>} Card type options.
 * @example
 * const options = CardTypesOptions('for_personal_use');
 */
export const CardTypesOptions = (activeValue) => OptionsList(cardTypeKeys, activeValue);

/**
 * Collection of helper utilities for card management, formatting, KYC, limits, and table column persistence.
 * @type {Object}
 * @example
 * import { cardsHelpers } from './cards.helpers';
 * const status = cardsHelpers.getStatus(card);
 */
export const cardsHelpers = {
  /**
   * Computes the used/limit amounts and usage percentage for a card's limit object.
   * @param {Object|null} limits - Limit object with limit and used properties.
   * @returns {{percent: number, limit: React.ReactElement, used: React.ReactElement}} Utilization data with formatted amounts.
   * @example
   * cardsHelpers.getCardLimitDetails({ limit: 1000, used: 350 });
   */
  getCardLimitDetails: (limits) => {
    const getAmountWithCurrencyCode = (value, showFractionalPart) =>
      amountHelpers.getAmountWithCurrencyCode(value, {showFractionalPart});

    let showFractionalPart;
    let percent = 100;
    let used = 0;
    let limit = 0;
    if (limits) {
      limit = limits?.limit || 0;
      used = limits?.used || 0;
      percent = used !== 0 ? (used / limit) * 100 : 0;
    }
    showFractionalPart = limit % 1 !== 0 || used % 1 !== 0;
    return {
      percent,
      limit: getAmountWithCurrencyCode(limit, showFractionalPart),
      used: getAmountWithCurrencyCode(used, showFractionalPart)
    };

  },
  /**
   * Determines the card type constant (DIGITAL, SUBSCRIPTION, or PHYSICAL) from a card object.
   * @param {Object|null} card - Card object with optional subscription_id and virtual_converted fields.
   * @returns {string} Card type constant.
   * @example
   * cardsHelpers.getCardType({ subscription_id: 'sub-1' }); // 'subscription'
   */
  getCardType: (card) => {
    if (!card || typeof card !== 'object') {
      return cardTypesConstants.DIGITAL;
    }

    if (card?.subscription_id) {
      return cardTypesConstants.SUBSCRIPTION;
    } else if (card?.physical) {
      return cardTypesConstants.PHYSICAL;
    } else {
      return cardTypesConstants.DIGITAL;
    }
  },
  /**
   * Returns the frontend card status constant (ACTIVE, FROZEN, PENDING, or TERMINATED) from a card object.
   * @param {Object|null} card - Card object with status_code and bank_id.
   * @returns {string} Card status constant.
   * @example
   * cardsHelpers.getStatus({ status_code: 'U', bank_id: '123' }); // 'active'
   */
  getStatus: (card) => {
    if (!card) return TERMINATED;

    const statusCode = card.status_code;
    const bankId = card?.bank_id;
    const hasValidBankId = objectHelpers.isValidValue(bankId);

    const statusMap = {
      [cardBackedStatus.DESTROYED]: TERMINATED,
      [cardBackedStatus.LOCK]: FROZEN,
      [cardBackedStatus.LOST]: TERMINATED,
      [cardBackedStatus.STOLEN]: TERMINATED,
      [cardBackedStatus.UNLOCK]: ACTIVE
    }
    if (!hasValidBankId && statusCode === null) {
      return PENDING;
    }

    return statusMap[statusCode] ?? TERMINATED;
  },
  /**
   * Returns the CSS class name representing the card icon type.
   * @param {Object} card - Card object with optional subscription_id and physical flag.
   * @returns {string} CSS class name string.
   * @example
   * cardsHelpers.getCardIconClassName({ physical: true }); // 'physical-card-type'
   */
  getCardIconClassName: (card) => {
    let className = card?.subscription_id ? 'subscription-card-type' : 'virtual-card-type';
    if (card?.physical) className = 'physical-card-type';

    return className;
  },
  /**
   * Returns a styled Space element containing the credit card icon with the appropriate card type class.
   * @param {Object} card - Card object passed to getCardIconClassName.
   * @returns {React.ReactElement} Styled card icon element.
   * @example
   * cardsHelpers.getCardIcon(card);
   */
  getCardIcon: (card) => {
    let spaceClassName = cardsHelpers.getCardIconClassName(card);

    return (
      <StyledCardPaymentTypeCardIconSpace
        className={spaceClassName}
      >
        {<WithLessCreditCardIcon />}
      </StyledCardPaymentTypeCardIconSpace>
    )
  },
  /**
   * Returns a StatusMark component for the card's lock_status.
   * @param {Object} data - Card data object with a lock_status property.
   * @returns {React.ReactElement} StatusMark component.
   * @example
   * cardsHelpers.getCardStatusMark({ lock_status: 'U' });
   */
  getCardStatusMark: (data) => (
    <StatusMark
      statuses={cardStatuses}
      statusCode={data.lock_status}
    />
  ),
  /**
   * Formats a 16-digit card number string into groups of four separated by spaces.
   * @param {string} value - Raw card number string (no spaces).
   * @returns {string} Formatted card number, e.g. '1234 5678 9012 3456'.
   * @example
   * cardsHelpers.getFormattedCardNumber('1234567890123456'); // '1234 5678 9012 3456'
   */
  getFormattedCardNumber: (value) => value.match(/.{1,4}/g).join(' '),
  /**
   * Extracts form field errors from a banking API validation response.
   * @param {Object} response - Response object with a detail array of error items.
   * @returns {Object.<string, string>} Map of field names to error messages.
   * @example
   * cardsHelpers.getResponseErrors({ detail: [{ type: 'value_error.missing', loc: ['body', 'email'], msg: 'Required' }] });
   */
  getResponseErrors: (response) => {
    const errors = {};
    const data = response.detail;
    if (typeof data === 'object') {
      data.forEach(err => {
        let locLength = err.loc.length;
        if (err.type.startsWith('value_error') && locLength >= 2) {
          let fieldName = err.loc[locLength - 1];
          if (['legal_person', 'legal_representative', 'ultimate_beneficiary_owner'].includes(fieldName)) {
            fieldName = 'email';
          }
          errors[fieldName] = err.msg;
        }
      });
    }
    return errors;
  },
  /**
   * Extracts an array of Ant Design form field error objects from a banking API validation response.
   * @param {Object} response - Response object with a detail array of error items.
   * @returns {Array<{name: string, errors: string[]}>} Array of field error objects for Ant Design Form.setFields.
   * @example
   * cardsHelpers.getBankingResponseErrorFields({ detail: [{ type: 'missing', loc: ['body', 'phone'], msg: 'Required' }] });
   */
  /**
   * Returns the error messages that getBankingResponseErrorFields cannot surface.
   *
   * That function maps an error onto an antd field name taken from `loc`. Anything it
   * cannot map - a model-level error whose `loc` stops at the body, a type it does not
   * recognise such as `enum`, or a field with no rendered input like the company-derived
   * `city` - was silently dropped, so the form looked like it had saved nothing for no
   * reason (H1MVP-4977). Pass the names the form actually renders to catch that last case.
   *
   * @param {Object} response - Server error response object.
   * @param {string[]} [knownFields] - Field names the calling form renders.
   * @returns {string[]} Human-readable messages with no home on the form.
   * @example
   * cardsHelpers.getBankingResponseGeneralErrors({detail: [{type: 'enum', loc: ['body', 'title'], msg: "Input should be 'M'"}]}, ['email']);
   * // ["title: Input should be 'M'"]
   */
  getBankingResponseGeneralErrors: (response, knownFields = []) => {
    const data = response?.detail;
    if (typeof data === 'string') return [data];
    if (!Array.isArray(data)) return [];
    const known = new Set(knownFields);
    return data.reduce((messages, err) => {
      const loc = Array.isArray(err?.loc) ? err.loc : [];
      const name = loc.length ? String(loc[loc.length - 1]) : undefined;
      const type = typeof err?.type === 'string' ? err.type : '';
      const mappable = (type.startsWith('value_error') || type === 'missing' || type === 'string_type')
        && loc.length >= 2;
      if (!mappable || !known.has(name)) {
        const message = [name, err?.msg].filter(Boolean).join(': ');
        if (message) messages.push(message);
      }
      return messages;
    }, []);
  },
  getBankingResponseErrorFields: (response) => {
    const fields = [];
    const data = response.detail;
    if (typeof data === 'object') {
      data.forEach(err => {
        if ((err.type.startsWith('value_error') || err.type === 'missing' || err.type === 'string_type') && err.loc.length >= 2) {
          fields.push({
            name: err.loc[err.loc.length - 1],
            errors: [err.msg]
          });
        }
      });
    }
    return fields;
  },
  /**
   * Builds the KYC document upload payload, encoding the file as base64 and appending a timestamp to the filename.
   * @param {string|null} userId - User ID to include in the payload, or null to omit.
   * @param {File} document - File object to encode.
   * @param {string} documentTypeId - Document type ID for the upload.
   * @returns {Promise<Object>} Resolved payload object with document_type_id, name, file_content_base64, and optional user_id.
   * @example
   * await cardsHelpers.getUploadedFilePayload('user-1', fileObject, 'PASSPORT');
   */
  getUploadedFilePayload: async (userId, document, documentTypeId) => {
    const fileContentBase64 = await fileHelpers.getBase64(document);
    const fileName = document?.name ? document.name.replace(/\.[^.]+$/, '') : '';
    const fileExtension = document?.name ? (document.name.match(/\.[^.]+$/)?.[0] || '') : '';
    const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
    return {
      document_type_id: documentTypeId,
      access_user_ip: '102.129.65.0',
      name: `${fileName}_${timestamp}${fileExtension}`,
      file_content_base64: fileContentBase64,
      ...(userId && { user_id: userId })
    }
  },
  /**
   * Maps tax residence form field values to the API payload shape.
   * @param {Object} fields - Form field values with taxResidence and tax_number.
   * @returns {{country: string, tax_payer_id: string}} API payload for tax residence.
   * @example
   * cardsHelpers.getTaxResidenceFormData({ taxResidence: 'fr', tax_number: 'FR123456789' });
   */
  getTaxResidenceFormData: (fields) => {
    const taxResidence = fields.taxResidence.toUpperCase();
    return {
      country: taxResidence,
      tax_payer_id: fields.tax_number
    };
  },
  /**
   * Derives a simplified KYC light status (VALIDATED or NONE) from the review and level values.
   * @param {string} review - KYC review status constant.
   * @param {string} level - KYC level status constant.
   * @returns {string} Light status constant (VALIDATED or NONE).
   * @example
   * cardsHelpers.getKYCStatus('VALIDATED', 'LIGHT'); // lightStatus.VALIDATED
   */
  getKYCStatus: (review, level) => {
    const isValidatedReview = review === kycReviewStatus.VALIDATED;
    const isValidatedLevel = [
      kycLevelStatus.LIGHT,
      kycLevelStatus.REGULAR,
      kycLevelStatus.STRONG
    ].includes(level);

    return isValidatedReview || isValidatedLevel
      ? lightStatus.VALIDATED
      : lightStatus.NONE;
  },
  /**
   * Returns true when the banking onboarding state indicates the card editing step is active.
   * @param {Object|null} state - Banking onboarding state with stage and state properties.
   * @returns {boolean} True if stage is 'card_stage' and state is 'card_editing'.
   * @example
   * cardsHelpers.checkIsAvailableBanking({ stage: 'card_stage', state: 'card_editing' }); // true
   */
  checkIsAvailableBanking: (state) => state && (state.stage === 'card_stage' && state.state === 'card_editing'),
  /**
   * Whether a card number satisfies the Luhn check digit.
   *
   * The number on this screen is read off a picture by OCR, so it can be wrong in ways nothing
   * else notices — length alone used to be the only test, and a misread that happened to produce
   * sixteen digits was handed to the clipboard as if it were correct (H1MVP-4737). The check digit
   * is what makes a bad read detectable: a random misread fails it about nine times in ten.
   *
   * @param {string} cardNumber - Digits, with or without separators.
   * @returns {boolean} True when the number is 16 digits and the checksum holds.
   * @example
   * cardsHelpers.isValidCardNumber('5555 5555 5555 4444'); // true
   */
  isValidCardNumber: (cardNumber) => {
    const digits = String(cardNumber ?? '').replace(/\D/g, '');
    if (digits.length !== 16) return false;
    let sum = 0;
    for (let i = 0; i < digits.length; i += 1) {
      let digit = Number(digits[digits.length - 1 - i]);
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    return sum % 10 === 0;
  },
  /**
   * Whether an OCR'd number agrees with the masked PAN the API returned for the same card.
   *
   * The strongest of the checks available: `masked_pan` comes from the server, so its last four
   * digits are authoritative. Anything disagreeing with them is certainly a misread.
   *
   * Returns true when there is no mask to compare against — absence of evidence must not reject a
   * number the checksum already accepted. The mask narrows the check, it does not gate it.
   *
   * @param {string} cardNumber - The recognised number.
   * @param {string} [maskedPan] - The masked PAN from the API, e.g. '512345******1234'.
   * @returns {boolean} False only when both are present and the last four digits differ.
   */
  matchesMaskedPan: (cardNumber, maskedPan) => {
    const digits = String(cardNumber ?? '').replace(/\D/g, '');
    const maskDigits = String(maskedPan ?? '').replace(/\D/g, '');
    if (digits.length < 4 || maskDigits.length < 4) return true;
    return digits.slice(-4) === maskDigits.slice(-4);
  },
  /**
   * Normalises an OCR'd card number, returning undefined unless it can be shown to be correct.
   *
   * @param {string} text - Raw card number text with spaces as read from OCR.
   * @param {Object} [options]
   * @param {string} [options.maskedPan] - Masked PAN from the API, used to confirm the last four.
   * @returns {string|undefined} 'XXXX XXXX XXXX XXXX', or undefined when the read cannot be trusted.
   * @example
   * cardsHelpers.formatCreditCardNumber('5555 5555 5555 4444'); // '5555 5555 5555 4444'
   * cardsHelpers.formatCreditCardNumber('1234 5678 9012 3456'); // undefined — fails the checksum
   */
  formatCreditCardNumber: (text, {maskedPan} = {}) => {
    const groups = String(text ?? '').trim().split(/\s+/);
    const [firstGroup = '', ...restGroups] = groups;

    // Tesseract reliably reads the leading 5 of a Mastercard as an S, and sometimes runs a stray
    // mark into the first group. Rebuilding that group around a known 5 repairs a real and common
    // misread, so it is kept — but as a CANDIDATE rather than an unconditional rewrite. Applied
    // to every read, as it was before, it also corrupts a first group that came back correct.
    const repairedFirstGroup = firstGroup.length >= 3
      ? `${MASTERCARD_PREFIX}${firstGroup.slice(firstGroup.length - 3)}`
      : firstGroup;

    // As-read first, so a clean read is never "repaired" into something else.
    const candidates = [
      [firstGroup, ...restGroups].join(''),
      [repairedFirstGroup, ...restGroups].join(''),
    ];

    const accepted = candidates
      .map((candidate) => candidate.replace(/\D/g, ''))
      .find((digits) => cardsHelpers.isValidCardNumber(digits)
        && cardsHelpers.matchesMaskedPan(digits, maskedPan));

    return accepted ? accepted.match(/.{1,4}/g).join(' ') : undefined;
  },
  /**
   * Uses Tesseract OCR to extract card number, owner, CVV, and expiry from a card image.
   * @param {string} image - Base64 image string or URL of the card image.
   * @returns {Promise<{cvv: string|undefined, expired: string|undefined, number: string|undefined, owner: string|undefined}>} Resolved card details.
   * @throws {string} Rejects with an error message string if OCR fails.
   * @example
   * const details = await cardsHelpers.getCardDetailsFromImage(base64ImageString);
   */
  getCardDetailsFromImage: (image, {maskedPan} = {}) => {
    return new Promise((resolve, reject) => {
      fileHelpers.recognizeImageToText(
        image,
        (result) => {
          try {
            // recognize card image
            let expired, cvv, owner, cardNumber;
            const resultText = result.data.text;
            // eslint-disable-next-line no-control-regex
            const separatedData = resultText.replace(/[^\x00-\x7F]/g, "").split(/\r?\n/).map(t => t.trim());
            const isOldTemplate = resultText.toLowerCase().indexOf('h1') >= 0;
            // recognize separate data start index
            const startIndex = isOldTemplate ? 0 : 1;

            const recognizeCvvData = (cvvData) => {
              // recognize cvv and expired values
              const cvvDataList = cvvData.split(' ');
              if (cvvDataList.length >= 3) {
                expired = cvvDataList[0];
                cvv = cvvData.slice(cvvDataList[0].length).replace(/\D+/g, "");
                // mark value as undefined if value is recognized with uncorrected length
                if (expired.length !== 5) expired = undefined;
                if (cvv.length !== 3) cvv = undefined;
              }
            }

            if (separatedData.length >= 5) {
              owner = separatedData[startIndex + 2].replaceAll('|', 'I');
              cardNumber = cardsHelpers.formatCreditCardNumber(separatedData[startIndex + 3], {maskedPan});
              recognizeCvvData(separatedData[startIndex + 4]);
            }

            // recognize cvv part of image if expired or cvv is recognized uncorrected
            if (expired === undefined || cvv === undefined) {
              const canvas = document.createElement('canvas'), // In memory canvas
                ctx = canvas.getContext('2d'),
                img = new Image();

              function handleLoadCvvDetails() {
                const width = img.width;
                const height = img.height;
                const sy = height * 0.7364;
                const sh = height - sy;
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(this, 0, sy, width, sh, 0, 0, width, sh);
                let base64Image = canvas.toDataURL(); // ("image/jpeg") for jpeg
                fileHelpers.recognizeImageToText(
                  base64Image,
                  (result) => {
                    try {
                      // eslint-disable-next-line no-control-regex
                      const separatedData = result.data.text.replace(/[^\x00-\x7F]/g, "").trim();
                      recognizeCvvData(separatedData);
                      resolve({
                        cvv,
                        expired,
                        number: cardNumber,
                        owner
                      });
                    } catch (e) {
                      reject(`An error occurred: ${e}`)
                    }
                  },
                  (err) => reject(err)
                )
              }

              img.onload = handleLoadCvvDetails;
              img.src = image;
            } else {
              resolve({
                cvv,
                expired,
                number: cardNumber,
                owner
              })
            }
          } catch (e) {
            reject(`An error occurred: ${e}`)
          }
        },
        (err) => reject(err)
      );
    })
  },
  /**
   * Reads the payment card limit from the Redux state's banking.companyDetails and returns limits for personal and subscription cards.
   * @param {Object} state - Redux state object with a banking slice.
   * @returns {{personalCardLimit: number, subscriptionCardLimit: number}} Card limit values.
   * @example
   * cardsHelpers.getPaymentCardLimit(store.getState());
   */
  getPaymentCardLimit: (state) => {
    let limit = 10000;
    try {
      limit = Number(state.banking.companyDetails.limit_payment_card);
    } catch {}
    return {personalCardLimit: limit, subscriptionCardLimit: limit};
  },
  /**
   * Converts an MDC ruleset facts-balance payload into card limit values.
   * Picks the fact matching the card's active limit period, falling back to the first available fact.
   * @param {Object|null} facts - Facts map from the ruleset facts-balance response.
   * @param {string} [period] - Active card limit period (all, month or year).
   * @returns {{limit: number, used: number, remaining: number}|null} Limit values, or null when no fact is available.
   * @example
   * cardsHelpers.getRulesetFactLimits({month_total_amount: {consumed_value: 0, defined_value: '10000', remaining_value: 10000}}, 'month');
   */
  getRulesetFactLimits: (facts, period) => {
    if (!facts) return null;

    const fact = facts[`${period}_total_amount`] || Object.values(facts)[0];
    if (!fact) return null;

    return {
      limit: Number(fact.defined_value) || 0,
      used: Number(fact.consumed_value) || 0,
      remaining: Number(fact.remaining_value) || 0
    };
  },
  /**
   * Extracts the active limit option, name, and value from a card's limit fields.
   * @param {Object} [card={}] - Card object with limit period amount fields.
   * @returns {{option: string, name: string, value: number}} Active limit period, field name, and amount.
   * @example
   * cardsHelpers.getCardLimit({ limit_month: 500 }); // { option: 'month', name: 'limit_month', value: 500 }
   */
  getCardLimit: (card = {}) => {
    let limitValue = 0;
    let limitName = '';
    let option = '';
    if (card) {
      cardLimitTypesList.forEach(key => {
        let value = card[key];
        if (typeof value === 'number' && value > 0) {
          limitValue = value;
          limitName = key;
        }
      });
      option = Object.values(cardLimitPeriodConstants).find(key => limitName.split('_').includes(key));
    }
    return {
      option,
      name: limitName,
      value: limitValue
    }
  },
  /**
   * Returns the total payment amount for the active limit period on a card.
   * @param {Object} [card={}] - Card object with total and limit period amount fields.
   * @returns {number} Total payment value for the active limit period, or 0 if not found.
   * @example
   * cardsHelpers.getCardTotalPaymentValue({ limit_month: 500, total_month: 200 }); // 200
   */
  getCardTotalPaymentValue: (card = {}) => {
    let value = 0;
    let name = '';
    if (card) {
      cardLimitTypesList.forEach(key => {
        let value = card[key];
        if (typeof value === 'number' && value > 0) name = key;
      });
      if (name) {
        name = name.replace('limit', 'total');
        value = card[name] || 0;
      }
    }
    return value;
  },
  /**
   * Builds the card limit update payload from form data, zeroing all non-active period limit keys.
   * @param {Object} formData - Form data with cardLimitPeriodFieldName and cardLimitFieldName fields.
   * @returns {Object} Payload object with all card limit type keys set to the limit or 0.
   * @example
   * cardsHelpers.getSetLimitPayload({ limit_period_short: 'month', card_limit: 1000 });
   */
  getSetLimitPayload: (formData) => {
    let data = {};
    let period = formData[subscriptionFormFields.cardLimitPeriodFieldName];
    let limit = formData[subscriptionFormFields.cardLimitFieldName];
    cardLimitTypesList.forEach(limitKey => {
      data = {...data, [limitKey]: limitKey.split('_').includes(period) ? limit : 0};
    })
    return data;
  },
  /**
   * Extracts and formats company details from a legalPerson banking record.
   * @param {Object|null} legalPerson - Legal person object with address, city, postcode, country, legal_name, legal_tva_number.
   * @returns {{address: string, city: string, country: string, name: string, vat: string}} Formatted company detail fields.
   * @example
   * cardsHelpers.getBankingCompanyDetails(legalPerson);
   */
  getBankingCompanyDetails: (legalPerson) => {
    const getProp = (propName, fallback = '') => objectHelpers.getObjProp(legalPerson, propName, fallback) || fallback;

    const countryCode = getProp('country');
    const countryName = localizationHelpers.getCountryByCode(countryCode)?.name || countryCode;
    const city = getProp('city');
    const postCode = getProp('postcode');
    const address1 = getProp('address1');
    const address = legalPerson ? `${address1}, ${postCode} ${city}, ${countryName}` : '';

    return {
      address,
      city,
      country: countryName,
      name: getProp('legal_name'),
      vat: getProp('legal_tva_number')
    };
  },
  /**
   * Retrieves the stored cards table column configuration from localStorage.
   * @returns {Array<string>|null} Stored column keys array or null if not set.
   * @example
   * cardsHelpers.getStoredCardsTableColumns();
   */
  getStoredCardsTableColumns: () => ls.get(storedCardTableColumns),
  /**
   * Persists the cards table column configuration to localStorage.
   * @param {Array<string>} value - Array of column key strings to store.
   * @returns {void}
   * @example
   * cardsHelpers.storeCardsTableColumns(['name', 'status', 'limit']);
   */
  storeCardsTableColumns: (value) => ls.set(storedCardTableColumns, value),
  /**
   * Returns the stored cards table column keys if available, falling back to the provided defaults.
   * @param {Array<string>} defaultKeys - Default column keys to use when nothing is stored.
   * @returns {Array<string>} Stored or default column keys.
   * @example
   * cardsHelpers.getDefaultCardsTableColumns(['name', 'status']);
   */
  getDefaultCardsTableColumns: (defaultKeys) => cardsHelpers.getStoredCardsTableColumns() || defaultKeys,
  /**
   * Filters out terminated cards from a list.
   * @param {Array<Object>} [cards=[]] - Array of card objects with a lock_status field.
   * @returns {Array<Object>} Cards that are not in TERMINATED status.
   * @example
   * cardsHelpers.filterValidCards(cards);
   */
  filterValidCards: (cards = []) => cards.filter(card => card.lock_status !== TERMINATED),
  /**
   * Returns true when the card's digitalization_status is ACTIVE.
   * @param {Object} card - Card object with a digitalization_status property.
   * @returns {boolean} True if the card is digitalized.
   * @example
   * cardsHelpers.isDigitalizedCard({ digitalization_status: 'ACTIVE' }); // true
   */
  isDigitalizedCard: (card) => objectHelpers.getObjProp(card, 'digitalization_status') === cardDigitalizationStatusConstants.ACTIVE,
  /**
   * Returns true when the given limit exceeds the maximum allowed limit.
   * @param {Object} params - Parameters object.
   * @param {number} [params.limit=0] - Limit value to check.
   * @param {number} [params.maxLimit=0] - Maximum permitted limit.
   * @returns {boolean} True if limit is greater than maxLimit.
   * @example
   * cardsHelpers.isTooHighLimit({ limit: 5000, maxLimit: 3000 }); // true
   */
  isTooHighLimit: ({limit = 0, maxLimit = 0}) => limit > maxLimit,
  /**
   * Returns the last 4 characters of a string, or 'N.A.' if the string is empty or nullish.
   * @param {string} value - Card number or masked PAN string.
   * @returns {string} Last 4 characters or 'N.A.'.
   * @example
   * cardsHelpers.getLast4Digits('1234567890123456'); // '3456'
   */
  getLast4Digits: (value) => {
    const len = value ? value.length : 0;
    return value ? value.substring(len - 4, len) : 'N.A.';
  },
  /**
   * Returns the last N characters of a card number string for masked display.
   * @param {string|null} cardNumber - Full or masked card number string.
   * @param {number} [symbolsLength=5] - Number of trailing characters to keep.
   * @returns {string} Trailing characters or empty string if cardNumber is falsy.
   * @example
   * cardsHelpers.hideCardNumber('1234567890123456', 5); // '23456'
   */
  hideCardNumber: (cardNumber, symbolsLength = 5) => {
    if (cardNumber) {
      const len = cardNumber.length;
      return cardNumber ? cardNumber.substring(len - symbolsLength, len) : '';
    } else {
      return ''
    }
  },
  /**
   * Maps a subscription bill type constant to the corresponding card limit period constant.
   * @param {string} billed - Subscription bill type constant (PASS_AS_YOU_GO, MONTHLY, or YEARLY).
   * @returns {string|undefined} Card limit period constant or undefined if the bill type is unrecognised.
   * @example
   * cardsHelpers.getCardLimitTypeFromBilled('monthly'); // 'month'
   */
  getCardLimitTypeFromBilled: (billed) => ({
    [subscriptionBillTypesConstants.PASS_AS_YOU_GO]: cardLimitPeriodConstants.ALL,
    [subscriptionBillTypesConstants.MONTHLY]: cardLimitPeriodConstants.MONTH,
    [subscriptionBillTypesConstants.YEARLY]: cardLimitPeriodConstants.YEAR
  })[billed] || undefined,
  /**
   * Returns true when the card's limit matches the budget limit and the limit period matches the billing period.
   * @param {Object} card - Card/form data with billed, budget_limit, card_limit, and limit_period_short fields.
   * @returns {boolean} True if a hard limit is selected.
   * @example
   * cardsHelpers.isHardLimitSelected(formValues);
   */
  isHardLimitSelected: (card) => {
    const gObjProp = (key, defaultValue) => objectHelpers.getObjProp(card, key, defaultValue);

    const billedPeriodValue = gObjProp(billedFieldName);
    const budgetLimitValue = gObjProp(budgetLimitFieldName);
    const cardLimitValue = gObjProp(cardLimitFieldName);
    const limitPeriodValue = gObjProp(limitPeriodShortFieldName);

    return budgetLimitValue === cardLimitValue && limitPeriodValue === cardsHelpers.getCardLimitTypeFromBilled(billedPeriodValue);
  },
  /**
   * Returns the required card detail fields (billed, budget limit, card limit, limit period) from form data with fallbacks.
   * @param {Object} data - Form or card data object.
   * @returns {Object} Object with billedFieldName, budgetLimitFieldName, cardLimitFieldName, and limitPeriodShortFieldName keys.
   * @example
   * cardsHelpers.getRequiredCardDetailsFields(formData);
   */
  getRequiredCardDetailsFields: (data) => {
    const gObjProp = (key, defaultValue) => data.hasOwnProperty(key) ? data[key] : defaultValue;

    const billedPeriodValue = gObjProp(billedFieldName);
    const budgetLimitValue = gObjProp(budgetLimitFieldName);
    const cardLimitValue = gObjProp(cardLimitFieldName, budgetLimitValue);
    const limitPeriodValue = gObjProp(limitPeriodShortFieldName) || cardsHelpers.getCardLimitTypeFromBilled(billedPeriodValue);

    return {
      [billedFieldName]: billedPeriodValue,
      [budgetLimitFieldName]: budgetLimitValue,
      [cardLimitFieldName]: cardLimitValue,
      [limitPeriodShortFieldName]: limitPeriodValue
    }
  },
  /**
   * Builds the card form field values object from a card and its separate limit data.
   * @param {Object} [card={}] - Card object providing billed and budget limit values.
   * @param {Object} [data={}] - Limit data object providing card limit and limit period.
   * @returns {Object} Card form field values keyed by subscription form field names.
   * @example
   * cardsHelpers.getCardFormFieldsFromLimits(card, limitsData);
   */
  getCardFormFieldsFromLimits: (card = {}, data = {}) => ({
    [billedFieldName]: card[billedFieldName],
    [budgetLimitFieldName]: card[budgetLimitFieldName] || 0,
    [cardLimitFieldName]: data[cardLimitFieldName] || 0,
    [limitPeriodShortFieldName]: data[cardLimitPeriodFieldName]
  }),
  /**
   * Returns the URL for a card details page, or null if cardId or userId is missing.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} [params.cardId] - Card ID.
   * @param {string} [params.userId] - User (owner) ID.
   * @returns {string|null} Card details URL or null.
   * @example
   * cardsHelpers.getCardDetailsUrl({ cardId: 'c1', userId: 'u1' });
   */
  getCardDetailsUrl: ({cardId, userId} = {}) => {
    if (!userId || !cardId) return null;
    return `${routes.cardsList}/${userId}/${cardId}`;
  },
  /**
   * Returns true when the card is both active and a physical card.
   * @param {Object} card - Card object with lock_status and physical properties.
   * @returns {boolean} True if the card is active and physical.
   * @example
   * cardsHelpers.checkIsActivePhysicalCard({ lock_status: 'active', physical: true }); // true
   */
  checkIsActivePhysicalCard: (card) => card?.lock_status === ACTIVE && card?.physical,
}
